// TempRoom.jsx — Temporary chat rooms: create, share, join as guest
import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../constants";
import { tempRooms as tempRoomsApi, auth as authApi } from "../api";
import { getSocket } from "../api";

// ── Small avatar ──────────────────────────────────────────────────────────────
function Avatar({ name, size = 28 }) {
  const initials = (name || "?").slice(0, 2).toUpperCase();
  const hue = name ? name.charCodeAt(0) * 13 % 360 : 0;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `hsl(${hue},50%,35%)`, display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: size * 0.36, fontWeight: 700, color: "#fff",
    }}>{initials}</div>
  );
}

// ── Host: Create & manage temp rooms ─────────────────────────────────────────
export function TempRoomManager({ user, onClose }) {
  const C = useTheme();
  const [rooms, setRooms] = useState([]);
  const [roomName, setRoomName] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try { setRooms(await tempRoomsApi.mine()); }
    catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create() {
    if (!roomName.trim() || creating) return;
    setCreating(true);
    try {
      const room = await tempRoomsApi.create(roomName.trim());
      setRooms(p => [room, ...p]);
      setRoomName("");
    } catch (e) { alert(e.message); }
    setCreating(false);
  }

  async function endRoom(id) {
    if (!window.confirm("End this room? All guests will be disconnected.")) return;
    try {
      await tempRoomsApi.end(id);
      setRooms(p => p.filter(r => r.id !== id));
    } catch (e) { alert(e.message); }
  }

  function copyLink(code) {
    const url = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  function copyCode(code) {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code + "_code");
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: 540, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text, fontFamily: C.font }}>🚪 Temp Rooms</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Share a link — guests join with just their name, no account needed</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
        </div>

        {/* Create new */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={roomName} onChange={e => setRoomName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && create()}
              placeholder="Room name (e.g. Family Hangout)…"
              style={{ flex: 1, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontSize: 14, padding: "9px 14px", outline: "none" }}/>
            <button onClick={create} disabled={creating || !roomName.trim()}
              style={{ background: C.accent, border: "none", borderRadius: 10, color: "#0A0C10", fontWeight: 800, fontSize: 14, padding: "9px 18px", cursor: "pointer", opacity: (!roomName.trim() || creating) ? 0.5 : 1, whiteSpace: "nowrap" }}>
              {creating ? "…" : "Create"}
            </button>
          </div>
        </div>

        {/* Room list */}
        <div style={{ flex: 1, overflow: "auto", padding: "10px 14px" }}>
          {loading && <div style={{ color: C.muted, textAlign: "center", padding: 24 }}>Loading…</div>}
          {!loading && rooms.length === 0 && (
            <div style={{ color: C.muted, textAlign: "center", padding: "32px 0", fontSize: 13 }}>
              No active rooms. Create one above to get a shareable link.
            </div>
          )}
          {rooms.map(room => {
            const joinUrl = `${window.location.origin}/join/${room.code}`;
            return (
              <div key={room.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{room.name}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      Created {new Date(room.created_at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {" · "}auto-closes after 5 min idle
                    </div>
                  </div>
                  <button onClick={() => endRoom(room.id)}
                    style={{ background: "#EF444422", border: "1px solid #EF444444", borderRadius: 8, color: "#EF4444", fontSize: 12, fontWeight: 700, padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                    End Room
                  </button>
                </div>

                {/* Join code */}
                <div style={{ background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Join Code</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: C.accent, letterSpacing: 3, fontFamily: "monospace" }}>{room.code.toUpperCase()}</div>
                  </div>
                  <button onClick={() => copyCode(room.code)}
                    style={{ background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 7, color: C.accent, fontSize: 12, padding: "5px 12px", cursor: "pointer", fontWeight: 700 }}>
                    {copied === room.code + "_code" ? "✓ Copied" : "Copy Code"}
                  </button>
                </div>

                {/* Full link */}
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{ flex: 1, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 10px", fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {joinUrl}
                  </div>
                  <button onClick={() => copyLink(room.code)}
                    style={{ background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 7, color: C.accent, fontSize: 12, padding: "7px 12px", cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {copied === room.code ? "✓ Copied!" : "Copy Link"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Guest: Join page — shown when visiting /join/:code ───────────────────────
// This is a standalone full-page component that handles the full guest flow:
// 1. Validate code  2. Enter name  3. Get guest token  4. Enter temp chat + voice
export function TempRoomJoinPage({ code }) {
  const C = useTheme();
  const [phase, setPhase] = useState("lookup"); // lookup | enter_name | in_room | ended | error
  const [room, setRoom] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [input, setInput] = useState("");
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  // Validate room code on load
  useEffect(() => {
    async function lookup() {
      try {
        const r = await tempRoomsApi.lookup(code);
        setRoom(r);
        setPhase("enter_name");
      } catch {
        setPhase("error");
        setError("This room doesn't exist or has already ended.");
      }
    }
    lookup();
  }, [code]);

  // Scroll to bottom on new messages
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function joinRoom() {
    if (!displayName.trim() || joining) return;
    setJoining(true);
    setError("");
    try {
      // Create guest account
      const { token: guestToken, user } = await authApi.guest(displayName.trim());
      setToken(guestToken);
      setUserId(user.id);

      // Load history
      const hist = await tempRoomsApi.messages(room.id);
      setMessages(hist);

      // Connect socket
      const sock = getSocket(guestToken);
      socketRef.current = sock;

      sock.emit("temp_room_join", { roomId: room.id, displayName: displayName.trim() });

      sock.on("temp_room_joined",  ({ room: r }) => { setRoom(r); setPhase("in_room"); });
      sock.on("temp_room_message", (msg) => setMessages(p => [...p, msg]));
      sock.on("temp_room_members", ({ members: m }) => setMembers(m));
      sock.on("temp_room_error",   ({ error: e }) => { setError(e); setPhase("error"); });

    } catch (e) {
      setError(e.message || "Failed to join");
    }
    setJoining(false);
  }

  function sendMessage() {
    if (!input.trim() || !socketRef.current) return;
    socketRef.current.emit("temp_room_message", { roomId: room.id, content: input.trim(), displayName: displayName.trim() });
    setInput("");
  }

  function leaveRoom() {
    socketRef.current?.emit("temp_room_leave", { roomId: room?.id });
    socketRef.current?.disconnect();
    setPhase("ended");
  }

  const containerStyle = {
    minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center",
    justifyContent: "center", padding: 20, boxSizing: "border-box",
    fontFamily: C.font || "sans-serif",
  };

  if (phase === "lookup") return (
    <div style={containerStyle}>
      <div style={{ color: C.accent, fontSize: 16 }}>Checking room…</div>
    </div>
  );

  if (phase === "error") return (
    <div style={containerStyle}>
      <div style={{ textAlign: "center", maxWidth: 360 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🚫</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Room Unavailable</div>
        <div style={{ fontSize: 14, color: C.muted }}>{error}</div>
      </div>
    </div>
  );

  if (phase === "ended") return (
    <div style={containerStyle}>
      <div style={{ textAlign: "center", maxWidth: 360 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>👋</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>You've left the room</div>
        <div style={{ fontSize: 14, color: C.muted }}>Thanks for joining. The room may still be active for others.</div>
      </div>
    </div>
  );

  if (phase === "enter_name") return (
    <div style={containerStyle}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: "36px 32px", width: "100%", maxWidth: 380, textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🍺</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 4, fontFamily: C.font }}>Join "{room?.name}"</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>Enter your name to join — no account needed</div>

        <input value={displayName} onChange={e => setDisplayName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && joinRoom()}
          autoFocus placeholder="Your name…" maxLength={32}
          style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 12, color: C.text, fontSize: 16, padding: "12px 16px", outline: "none", boxSizing: "border-box", textAlign: "center", marginBottom: 12 }}/>

        {error && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 10 }}>{error}</div>}

        <button onClick={joinRoom} disabled={!displayName.trim() || joining}
          style={{ width: "100%", background: C.accent, border: "none", borderRadius: 12, color: "#0A0C10", fontWeight: 800, fontSize: 16, padding: "13px 0", cursor: "pointer", opacity: (!displayName.trim() || joining) ? 0.5 : 1 }}>
          {joining ? "Joining…" : "Enter Room →"}
        </button>
      </div>
    </div>
  );

  // ── In-room UI ──────────────────────────────────────────────────────────────
  return (
    <div style={{ height: "100vh", background: C.bg, display: "flex", flexDirection: "column", fontFamily: C.font || "sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: C.card }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>🚪 {room?.name}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{members.length} in room · auto-closes after 5 min idle</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Members */}
          <div style={{ display: "flex", gap: -4 }}>
            {members.slice(0, 5).map((m, i) => (
              <div key={m.user_id} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 10 - i }}>
                <Avatar name={m.display_name} size={28}/>
              </div>
            ))}
            {members.length > 5 && <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: C.muted, marginLeft: -8 }}>+{members.length - 5}</div>}
          </div>
          <button onClick={leaveRoom}
            style={{ background: "#EF444422", border: "1px solid #EF444444", borderRadius: 8, color: "#EF4444", fontSize: 12, fontWeight: 700, padding: "6px 12px", cursor: "pointer" }}>
            Leave
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", color: C.muted, padding: "40px 0", fontSize: 13 }}>
            No messages yet — say hello!
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} style={{ display: "flex", gap: 10, padding: "5px 0", alignItems: "flex-start" }}>
            <Avatar name={msg.display_name} size={30}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                {msg.display_name}
                {msg.user_id === userId && <span style={{ fontSize: 10, color: C.muted, fontWeight: 400, marginLeft: 6 }}>you</span>}
                <span style={{ fontSize: 10, color: C.muted, fontWeight: 400, marginLeft: 6 }}>
                  {new Date(msg.created_at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.4, wordBreak: "break-word" }}>{msg.content}</div>
            </div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{ padding: "10px 14px 14px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, flexShrink: 0, background: C.card }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type a message…"
          style={{ flex: 1, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 20, color: C.text, fontSize: 14, padding: "10px 16px", outline: "none" }}/>
        <button onClick={sendMessage} disabled={!input.trim()}
          style={{ background: input.trim() ? C.accent : C.inputBg, border: "none", borderRadius: "50%", cursor: "pointer", color: input.trim() ? "#0A0C10" : C.muted, width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s", fontWeight: 900, fontSize: 16 }}>
          ›
        </button>
      </div>
    </div>
  );
}
